/**
 * Species: bodies on a rig of their own. A humanoid is posed on the human skeleton (rig.ts) by the
 * human clips (clips.ts); a SPECIES brings everything that skeleton and those clips are, as data --
 * a deer, a wolf, a spider, a bird:
 *
 *   - its RIG: named joints at their rest positions, each hung on a parent, and which chains of them
 *     are LEGS (root, knee, ankle and optionally a foot segment below it, planted by IK);
 *   - its GAITS: locomotion generated from a handful of numbers -- how far a cycle carries it, how
 *     long each foot stays down, when each leg steps (a four-beat walk, a trot's diagonal pairs, a
 *     gallop) -- and whatever else rocks or swings in time (the body's bob, the head's nod, a tail);
 *   - its CLIPS: key poses eased between, as every keyframed clip is (an idle, an attack, a fall);
 *   - which of those plays each of the game's ACTIONS;
 *   - its BODIES: models in the expression language (model.ts, slot `species`) hung on the posed
 *     joints, drawn by the same raster in the same eight facings as everything else.
 *
 * Content: `content/game/sprites/ember/species.json`, bound here. A species body is drawn under its
 * own name (planSpeciesAnimation; generate.ts hands it any name that is not a humanoid's).
 *
 * Content writes angles in DEGREES and lengths in world pixels. Model space is the humanoid's: y up
 * (the ground is 0), +z the way the body faces, +x its left.
 */
import { bindContent } from "../content/catalog"
import type { EmberAnimationPlan, EmberEquipment, EmberJoints } from "./generate"
import { EmberLook, Primitive, equipmentPrimitives, scalePrimitives } from "./body"
import { LPC_ACTION_DURATION_MS, clipTime } from "./clips"
import { EmberRaceRule, setEmberSpeciesRaces } from "./humanoids"
import { EmberModel, Json, LetList, emberDesign, noteEmberContent, registerEmberSpeciesModels, runEmberSpeciesModel, setPreviewEmberSpeciesModels } from "./model"
import { rampFrom } from "./palette"
import { FACINGS, Facing, Frame, RasterOptions, frameFoot, projectPoint, rasterize } from "./raster"
import { DEFAULT_PROPORTIONS, LimbJoints, Proportions, Skeleton, solveTwoBone } from "./rig"
import { DEFAULT_STYLE, EMBER_STYLES } from "./styles"
import { IDENTITY, Mat3, Vec3, add, apply, cross, deg, dot, ease, length, mul, normalize, rotX, rotY, rotZ, scale, sub } from "./vec"

// ---- content -------------------------------------------------------------------------------

/** A joint as content writes it: its name, its parent's (null for the one root) and where it is at rest. */
export type SpeciesJointSource = [string, string | null, [number, number, number]]

export interface SpeciesLegSource {
    /** A parent-to-child chain: root (hip, shoulder), knee, ankle, and optionally the foot's tip below it. */
    joints: string[]
    /**
     * Which way the foot segment (ankle to tip) folds when the leg lifts: 1 tips the ankle forward of
     * the tip (a foreleg's hoof flipping back under it), -1 back (a hind cannon swinging forward).
     * By default the opposite of the knee's bend.
     */
    fold?: 1 | -1
}

/**
 * Where a species holds things -- a hand, its jaws, the tip of a tail -- so the game's weapons and
 * shields (equipment.json's held and shield models, written for a person's hands) are drawn in it.
 * `grip` is which way a held tool points from that joint AT REST (a sword carried up and ahead: [0,
 * 0.5, 0.85]); `edge` which way a blade's edge faces (square to grip; by default the way that faces
 * down). Both turn with the joint, frame by frame, however its arm is turned.
 */
export interface SpeciesGripSource {
    /** The joint it holds with. */
    hand: string
    /** The joints of its arm above that, if it has one (by default the hand's parent and that one's). */
    elbow?: string
    shoulder?: string
    grip: [number, number, number]
    edge?: [number, number, number]
}

/** Its grips: R, the weapon hand; L, the other (a shield, a bow's grip, a two-handed weapon's second hand). */
export interface SpeciesGripsSource {
    R?: SpeciesGripSource
    L?: SpeciesGripSource
    /** The joint a slung shield and a fist's inset follow (default its chest, else its root). */
    torso?: string
    /** How big what it holds is: 1 a person's sword, 0.7 a goblin's. */
    size?: number
}

/** A swing in time with a cycle: a joint's turn (degrees), or the whole body's rise and shift (pixels). */
export interface SpeciesMotion {
    /** A joint, or `root` for the whole body. */
    joint: string
    /** pitch (+ nods forward/down), yaw (+ toward its left), roll (+ toward its left); rise, shift (forward), side (toward its left) on the root only. */
    axis: "pitch" | "yaw" | "roll" | "rise" | "shift" | "side"
    amplitude: number
    /** Whole swings per cycle (a walk's bob is 2: once per step). */
    perCycle?: number
    /** Where in the cycle it starts, 0..1. */
    phase?: number
    /** Held throughout, besides the swing. */
    offset?: number
}

/** Locomotion as a few numbers (sampleGait). */
export interface SpeciesGaitSource {
    about?: string
    frames: number
    /** Authored time per frame; the client re-times a gait from ground speed (`stride`). */
    frameMs: number
    /** Ground one cycle covers, in pixels: each foot moves on exactly this far per cycle. */
    stride: number
    /** Share of the cycle each foot is down: over 0.5 always one down per pair, under it a moment of flight. */
    duty: number
    /** When each leg's foot lands, 0..1 of the cycle, by leg. A leg left out stands. */
    phases: Record<string, number>
    /** How high the ankle lifts mid-swing, in pixels (the foot hangs below it, folded): one for all, or by leg. */
    lift: number | Record<string, number>
    /** How far the foot segment folds mid-swing, in degrees (each leg's `fold` says which way): one for all, or by leg. */
    fold?: number | Record<string, number>
    /** Where the feet land and leave, forward of where they stand at rest, in pixels. */
    reach?: number
    /** The body raised (+) or crouched (-) throughout, in pixels. */
    rise?: number
    /** Everything else that moves in time: the body's bob and rock, the head's nod, a tail's swing. */
    motion?: SpeciesMotion[]
}

/**
 * One key pose. A leg is planted where `feet` puts it -- [x, y, z] from where it stands at rest, in
 * the body's ground frame, and optionally the foot segment's fold in degrees -- or, `free`, it follows
 * the body like any other joint (a leg tucked in a leap, stiff in a fall). A leg not named stands.
 */
export interface SpeciesKeySource {
    t: number
    /** The whole body: raised, moved forward and toward its left, in pixels... */
    rise?: number
    shift?: number
    side?: number
    /** ...and turned about its root joint, in degrees (pitch + forward/down, roll + toward its left). */
    pitch?: number
    yaw?: number
    roll?: number
    /** Joints' own turns, [pitch, yaw, roll] in degrees, by joint. */
    turns?: Record<string, [number, number, number]>
    feet?: Record<string, [number, number, number] | [number, number, number, number] | "free">
}

export interface SpeciesClipSource {
    about?: string
    frames: number
    frameMs: number
    loop?: boolean
    keys: SpeciesKeySource[]
}

/** A body of the species: one of its models, with the values of its parameters, at a size. */
export interface SpeciesBodySource {
    about?: string
    model: string
    params?: Record<string, Json>
    /** Drawn this much bigger than the rig (a stag over a doe, a fawn under): its strides too. */
    scale?: number
}

export interface SpeciesSource {
    about?: string
    /** The style whose raster draws it (styles.ts), realist by default, and changes over that: pitch in degrees. */
    style?: string
    raster?: Record<string, Json>
    /** The frame it is drawn on, in pixels. */
    frame: number
    /** How far a side facing is turned from profile toward the camera, in degrees. */
    sideTurn?: number
    joints: SpeciesJointSource[]
    legs: Record<string, SpeciesLegSource>
    /** Named numbers its models read as `p` (girths, lengths). */
    proportions?: Record<string, Json>
    gaits: Record<string, SpeciesGaitSource>
    clips: Record<string, SpeciesClipSource>
    /** Which gait or clip plays each of the game's actions (idle, walk, run, slash, thrust, shoot, cast, hurt, climb). */
    actions: Record<string, string>
    /** Where effects find it on a frame: its head and two "hands" (joint names); its grips' hands win. */
    points?: { head?: string; hands?: [string, string] }
    /** Where it holds weapons, if it can: what the game's held and shield models are drawn in. */
    grips?: SpeciesGripsSource
    bodies: Record<string, SpeciesBodySource>
}

export interface EmberSpeciesFile {
    version: 1
    about?: string
    /** Helpers every species model sees, over the body layer's. */
    library?: LetList
    /** The species' body models (slot `species`), by name. */
    models: Record<string, EmberModel>
    species: Record<string, SpeciesSource>
    /** Which race each body draws: the first rule whose race (and sex) matches (as humanoids.json's races; a humanoid rule wins). */
    races?: EmberRaceRule[]
}

// ---- the rig --------------------------------------------------------------------------------

interface RigLeg {
    name: string
    /** Joint indices: root, knee, ankle[, tip]. */
    chain: number[]
    /** Root to knee, knee to ankle. */
    a: number
    b: number
    /** From the tip up to the ankle, at rest (the foot segment), when the leg has one. */
    footUp?: Vec3
    /** Which way the knee bends at rest, in the frame of the joint the leg hangs from. */
    pole: Vec3
    fold: number
    /** Where the leg's lowest joint stands at rest: what its feet are placed relative to. */
    contact: Vec3
}

export interface SpeciesRig {
    names: string[]
    parent: number[]
    rest: Vec3[]
    index: Record<string, number>
    legs: RigLeg[]
}

const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k)

/** The rig a species' joints and legs describe. Throws on one that does not hold together (validation says why first). */
export const buildRig = (joints: SpeciesJointSource[], legs: Record<string, SpeciesLegSource>): SpeciesRig => {
    const index: Record<string, number> = Object.create(null)
    const names: string[] = [], parent: number[] = [], rest: Vec3[] = []
    joints.forEach(([name, of, at], i) => {
        if (hasOwn(index, name)) throw new Error(`joint ${name} twice`)
        if (of !== null && !hasOwn(index, of)) throw new Error(`joint ${name}: its parent ${of} comes first`)
        index[name] = i
        names.push(name)
        parent.push(of === null ? -1 : index[of])
        rest.push([at[0], at[1], at[2]])
    })
    const rigLegs = Object.entries(legs).map(([name, leg]): RigLeg => {
        const chain = leg.joints.map(j => {
            if (!hasOwn(index, j)) throw new Error(`leg ${name}: no joint ${j}`)
            return index[j]
        })
        for (let k = 1; k < chain.length; k++) if (parent[chain[k]] !== chain[k - 1]) throw new Error(`leg ${name}: ${names[chain[k]]} does not hang from ${names[chain[k - 1]]}`)
        const [r, m, e, tip] = chain
        const toEnd = sub(rest[e], rest[r])
        const along = normalize(toEnd)
        const off = sub(rest[m], rest[r])
        let pole = sub(off, scale(along, dot(off, along)))
        if (length(pole) < 1e-6) pole = [0, 0, 1]
        pole = normalize(pole)
        return {
            name,
            chain,
            a: length(sub(rest[m], rest[r])),
            b: length(sub(rest[e], rest[m])),
            ...(tip !== undefined ? { footUp: sub(rest[e], rest[tip]) } : {}),
            pole,
            fold: leg.fold ?? (pole[2] > 0 ? -1 : 1),
            contact: rest[tip ?? e],
        }
    })
    return { names, parent, rest, index, legs: rigLegs }
}

/** Where a leg's foot goes: its lowest joint, and how far its foot segment is folded (radians). */
export interface SpeciesFoot {
    at: Vec3
    fold: number
    /**
     * How far the foot segment swings with the whole leg, 0..1 (1 unless given): a planted foot's does
     * -- the leg pivots over it as one column -- a foot carried through the air hangs folded as it is.
     */
    swing?: number
}

/** A pose of a species: the whole body, each joint's own turn, and where the planted feet are. */
export interface SpeciesPose {
    /** The root joint moved from rest. */
    root: Vec3
    /** The whole body turned about its root joint. */
    rootRotation: Mat3
    turns: Record<string, Mat3>
    /** Planted feet by leg; a leg without one follows the body (its joints' turns). */
    feet: Record<string, SpeciesFoot | undefined>
}

/** A solved pose: where each joint is, and the frame each is turned to. What a species model hangs on. */
export interface SpeciesSkeleton {
    at: Record<string, Vec3>
    basis: Record<string, Mat3>
    /** The way the body faces on the ground: its yaw alone. */
    heading: Mat3
}

/** The rotation that turns direction `u` onto direction `v` the shortest way round -- or `share` of the way. */
export const alignTo = (u: Vec3, v: Vec3, share = 1): Mat3 => {
    const a = normalize(u), b = normalize(v)
    const cosine = dot(a, b)
    const cr = cross(a, b)
    const sine = length(cr)
    if (sine < 1e-9 && cosine > 0) return IDENTITY
    // Opposite: half a turn about any axis square to them.
    const axis = sine < 1e-9 ? normalize(Math.abs(a[0]) < 0.9 ? cross(a, [1, 0, 0]) : cross(a, [0, 1, 0])) : scale(cr, 1 / sine)
    const angle = share * Math.atan2(sine, cosine)
    const c = share === 1 && sine >= 1e-9 ? cosine : Math.cos(angle)
    const s = share === 1 && sine >= 1e-9 ? sine : Math.sin(angle)
    const [x, y, z] = axis
    const t = 1 - c
    // Rodrigues: I + sin K + (1 - cos) K^2, about the unit axis.
    return [
        c + x * x * t, x * y * t - z * s, x * z * t + y * s,
        y * x * t + z * s, c + y * y * t, y * z * t - x * s,
        z * x * t - y * s, z * y * t + x * s, c + z * z * t,
    ]
}

/** The yaw alone of a body turned by `r`: the frame its feet are planted in. */
const headingOf = (r: Mat3): Mat3 => {
    const f = apply(r, [0, 0, 1])
    if (Math.hypot(f[0], f[2]) > 1e-6) return rotY(Math.atan2(f[0], f[2]))
    const u = apply(r, [0, 1, 0])
    return rotY(Math.atan2(-u[0], -u[2]))
}

/**
 * Pose the rig: each joint where its parent's turn carries it (forward kinematics), each leg with a
 * planted foot solved down to that foot (two-bone IK to the ankle, the foot segment swung with the
 * whole leg and folded), the knee bending the way it bends at rest.
 */
export const solveSpecies = (rig: SpeciesRig, pose: SpeciesPose): SpeciesSkeleton => {
    const n = rig.names.length
    const P: Vec3[] = new Array(n), R: Mat3[] = new Array(n)
    const done = new Uint8Array(n)
    const heading = headingOf(pose.rootRotation)
    const legAt = new Map(rig.legs.map(leg => [leg.chain[0], leg]))
    for (let i = 0; i < n; i++) {
        if (done[i]) continue
        const q = rig.parent[i]
        const turn = pose.turns[rig.names[i]]
        if (q < 0) {
            R[i] = turn ? mul(pose.rootRotation, turn) : pose.rootRotation
            P[i] = add(rig.rest[i], pose.root)
        } else {
            R[i] = turn ? mul(R[q], turn) : R[q]
            P[i] = add(P[q], apply(R[q], sub(rig.rest[i], rig.rest[q])))
        }
        done[i] = 1
        const leg = legAt.get(i)
        const foot = leg && pose.feet[leg.name]
        if (!leg || !foot) continue
        const [r, m, e, tip] = leg.chain
        const hung = q < 0 ? pose.rootRotation : R[q]
        // The foot segment swings with the leg as a whole -- as far as the line from the foot to the
        // leg's root has turned since rest -- and folds on top of that.
        const swing = alignTo(apply(heading, sub(rig.rest[r], leg.contact)), sub(P[r], foot.at), foot.swing ?? 1)
        const ankle = leg.footUp ? add(foot.at, apply(swing, apply(heading, apply(rotX(foot.fold * leg.fold), leg.footUp)))) : foot.at
        const solved = solveTwoBone(P[r], ankle, leg.a, leg.b, apply(hung, leg.pole))
        P[m] = solved.mid
        P[e] = solved.end
        R[r] = mul(alignTo(apply(hung, sub(rig.rest[m], rig.rest[r])), sub(P[m], P[r])), hung)
        R[m] = mul(alignTo(apply(R[r], sub(rig.rest[e], rig.rest[m])), sub(P[e], P[m])), R[r])
        done[m] = done[e] = 1
        if (tip !== undefined) {
            // Out of reach, the ankle stops short and the foot comes with it.
            P[tip] = add(P[e], sub(foot.at, ankle))
            R[e] = mul(alignTo(apply(R[m], sub(rig.rest[tip], rig.rest[e])), sub(P[tip], P[e])), R[m])
            R[tip] = R[e]
            done[tip] = 1
        }
    }
    const at: Record<string, Vec3> = {}, basis: Record<string, Mat3> = {}
    rig.names.forEach((name, i) => { at[name] = P[i]; basis[name] = R[i] })
    return { at, basis, heading }
}

// ---- clips -----------------------------------------------------------------------------------

/** A species' clip: a gait or keyed poses, sampled at any `t` from 0 to 1. */
export interface SpeciesClip {
    name: string
    frames: number
    frameMs: number
    loop: boolean
    /** Ground one cycle covers (a gait's). */
    strideCycle?: number
    sample: (t: number) => SpeciesPose
}

const turnOf = (pitch: number, yaw: number, roll: number): Mat3 => mul(rotY(deg(yaw)), mul(rotX(deg(pitch)), rotZ(deg(roll))))
const TAU = Math.PI * 2

/**
 * A gait: each leg planted for `duty` of the cycle, its foot carried back under the body as far as
 * the body travels meanwhile, then lifted, folded and swung forward to land again; the body's other
 * motions swinging in time.
 */
export const sampleGait = (rig: SpeciesRig, g: SpeciesGaitSource) => (t: number): SpeciesPose => {
    const angles: Record<string, [number, number, number]> = {}
    const body = { rise: g.rise ?? 0, shift: 0, side: 0, pitch: 0, yaw: 0, roll: 0 }
    for (const m of g.motion ?? []) {
        const v = (m.offset ?? 0) + m.amplitude * Math.sin(TAU * ((m.perCycle ?? 1) * t + (m.phase ?? 0)))
        if (m.joint === "root") body[m.axis] += v
        else {
            const a = (angles[m.joint] ??= [0, 0, 0])
            a[m.axis === "pitch" ? 0 : m.axis === "yaw" ? 1 : 2] += v
        }
    }
    const turns: Record<string, Mat3> = {}
    for (const [joint, [p, y, r]] of Object.entries(angles)) turns[joint] = turnOf(p, y, r)
    const feet: Record<string, SpeciesFoot> = {}
    const step = g.stride * g.duty
    const byLeg = (v: number | Record<string, number> | undefined, leg: string) => (typeof v === "number" ? v : v?.[leg] ?? 0)
    for (const leg of rig.legs) {
        const phase = g.phases[leg.name]
        if (phase === undefined) { feet[leg.name] = { at: leg.contact, fold: 0 }; continue }
        const q = (((t - phase) % 1) + 1) % 1
        const reach = g.reach ?? 0
        if (q < g.duty) {
            feet[leg.name] = { at: add(leg.contact, [0, 0, reach + step * (0.5 - q / g.duty)]), fold: 0 }
            continue
        }
        // Carried through the air: the ankle lifts and swings forward, the foot hanging from it folded.
        const s = (q - g.duty) / (1 - g.duty)
        const arc = Math.sin(Math.PI * s)
        const fold = deg(byLeg(g.fold, leg.name)) * Math.sin(Math.PI * Math.min(1, s * 1.2))
        const lifted: Vec3 = [0, byLeg(g.lift, leg.name) * arc, reach + step * (ease(s) - 0.5)]
        const hang = leg.footUp ? sub(apply(rotX(fold * leg.fold), leg.footUp), leg.footUp) : [0, 0, 0] as Vec3
        feet[leg.name] = { at: sub(add(leg.contact, lifted), hang), fold, swing: 1 - arc }
    }
    return {
        root: [body.side, body.rise, body.shift],
        rootRotation: turnOf(body.pitch, body.yaw, body.roll),
        turns,
        feet,
    }
}

const mix = (a: number | undefined, b: number | undefined, u: number) => (a ?? 0) + ((b ?? 0) - (a ?? 0)) * u

/**
 * Key poses eased between. A leg planted in one key and free in the next is carried from where it
 * was planted to where the body would hold it, so it never jumps.
 */
export const sampleSpeciesKeys = (rig: SpeciesRig, keys: SpeciesKeySource[]) => (t: number): SpeciesPose => {
    let i = 0
    while (i < keys.length - 2 && t > keys[i + 1].t) i++
    const a = keys[i], b = keys[i + 1]
    const u = ease(b.t > a.t ? (t - a.t) / (b.t - a.t) : 0)
    const turns: Record<string, Mat3> = {}
    for (const joint of new Set([...Object.keys(a.turns ?? {}), ...Object.keys(b.turns ?? {})])) {
        const ta = a.turns?.[joint] ?? [0, 0, 0], tb = b.turns?.[joint] ?? [0, 0, 0]
        turns[joint] = turnOf(mix(ta[0], tb[0], u), mix(ta[1], tb[1], u), mix(ta[2], tb[2], u))
    }
    const pose: SpeciesPose = {
        root: [mix(a.side, b.side, u), mix(a.rise, b.rise, u), mix(a.shift, b.shift, u)],
        rootRotation: turnOf(mix(a.pitch, b.pitch, u), mix(a.yaw, b.yaw, u), mix(a.roll, b.roll, u)),
        turns,
        feet: {},
    }
    const heading = headingOf(pose.rootRotation)
    const planted = (leg: RigLeg, f: [number, number, number] | [number, number, number, number] | "free" | undefined) =>
        f === "free" ? undefined : { at: add(leg.contact, apply(heading, [f?.[0] ?? 0, f?.[1] ?? 0, f?.[2] ?? 0])), fold: deg(f?.[3] ?? 0) }
    // Free legs follow the body; where one key frees a leg the other plants, blend toward the free pose.
    let free: SpeciesSkeleton | undefined
    for (const leg of rig.legs) {
        const fa = planted(leg, a.feet?.[leg.name]), fb = planted(leg, b.feet?.[leg.name])
        if (fa && fb) pose.feet[leg.name] = { at: add(fa.at, scale(sub(fb.at, fa.at), u)), fold: mix(fa.fold, fb.fold, u) }
        else if (fa || fb) {
            free ??= solveSpecies(rig, { ...pose, feet: {} })
            const lowest = rig.names[leg.chain[leg.chain.length - 1]]
            const held = { at: free.at[lowest], fold: 0 }
            const [from, to] = fa ? [fa, held] : [held, fb!]
            pose.feet[leg.name] = { at: add(from.at, scale(sub(to.at, from.at), u)), fold: mix(from.fold, to.fold, u) }
        }
    }
    return pose
}

// ---- the species ------------------------------------------------------------------------------

export interface EmberSpecies {
    name: string
    source: SpeciesSource
    rig: SpeciesRig
    /** Gaits and clips by name. */
    clips: Record<string, SpeciesClip>
}

export interface EmberSpeciesBody extends SpeciesBodySource {
    species: string
}

/** The species, and every body of every species by its name. Filled in place from content. */
export const EMBER_SPECIES: Record<string, EmberSpecies> = Object.create(null)
export const EMBER_SPECIES_BODIES: Record<string, EmberSpeciesBody> = Object.create(null)

const speciesOf = (name: string, source: SpeciesSource): EmberSpecies => {
    const rig = buildRig(source.joints, source.legs)
    const clips: Record<string, SpeciesClip> = {}
    for (const [gait, g] of Object.entries(source.gaits ?? {}))
        clips[gait] = { name: gait, frames: g.frames, frameMs: g.frameMs, loop: true, strideCycle: g.stride, sample: sampleGait(rig, g) }
    for (const [clip, c] of Object.entries(source.clips ?? {}))
        clips[clip] = { name: clip, frames: c.frames, frameMs: c.frameMs, loop: !!c.loop, sample: sampleSpeciesKeys(rig, c.keys) }
    return { name, source, rig, clips }
}

let bound: EmberSpeciesFile | undefined
/** What a tool is previewing over the game's: a draft species, its models and its race. */
export type EmberSpeciesPreview = Partial<Pick<EmberSpeciesFile, "species" | "models" | "library" | "races">>
let previewing: EmberSpeciesPreview | undefined

/** The game's species with any preview laid over them (a previewed species replaces one of its name). */
const laidOver = () => {
    for (const k of Object.keys(EMBER_SPECIES)) delete EMBER_SPECIES[k]
    for (const k of Object.keys(EMBER_SPECIES_BODIES)) delete EMBER_SPECIES_BODIES[k]
    for (const [name, source] of Object.entries({ ...(bound?.species ?? {}), ...(previewing?.species ?? {}) })) {
        let species: EmberSpecies
        // A draft being edited may not hold together yet: it is left out until it does (validation says why).
        try { species = speciesOf(name, source) } catch { continue }
        EMBER_SPECIES[name] = species
        for (const [body, b] of Object.entries(source.bodies ?? {})) EMBER_SPECIES_BODIES[body] = { ...b, species: name }
    }
    setEmberSpeciesRaces([...(previewing?.races ?? []), ...(bound?.races ?? [])], Object.keys(EMBER_SPECIES_BODIES))
}

/** Fill the species from content's species.json (bound below): their rigs, clips, bodies, models and races. */
export const registerEmberSpecies = (file: EmberSpeciesFile | undefined): void => {
    bound = file
    registerEmberSpeciesModels(file)
    laidOver()
    noteEmberContent("species", file)
}

/**
 * A species a tool is previewing, laid over the game's: a draft is drawn under its bodies' names
 * before it exists. Undefined clears it.
 */
export const setPreviewEmberSpecies = (preview: EmberSpeciesPreview | undefined): void => {
    previewing = preview
    setPreviewEmberSpeciesModels(preview ? { library: preview.library, models: preview.models } : undefined)
    laidOver()
    noteEmberContent("species-preview", preview)
}

/** The game's species as content writes them. */
export const emberSpeciesFile = (): EmberSpeciesFile | undefined => bound

export const isEmberSpeciesBody = (name: string | undefined): boolean => !!name && hasOwn(EMBER_SPECIES_BODIES, name)

// ---- drawing ---------------------------------------------------------------------------------

/** Yaw that turns the body's +z toward each screen facing: a side facing turned `sideTurn` from profile. */
export const speciesYaw = (facing: Facing, sideTurn: number): number => {
    switch (facing) {
        case "front": return 0
        case "back": return Math.PI
        case "frontright": return Math.PI / 4
        case "frontleft": return -Math.PI / 4
        case "backright": return (3 * Math.PI) / 4
        case "backleft": return (-3 * Math.PI) / 4
        case "right": return Math.PI / 2 - sideTurn
        case "left": return -(Math.PI / 2 - sideTurn)
    }
}

/** The share of a stride that crosses the screen in a facing (all of it but where the body is turned toward the camera). */
const acrossScreen = (facing: Facing, sideTurn: number) => (facing === "left" || facing === "right" ? Math.cos(sideTurn) : 1)

const turned = (prims: Primitive[], yaw: Mat3): Primitive[] =>
    prims.map(prim =>
        prim.kind === "capsule"
            ? { ...prim, a: apply(yaw, prim.a), b: apply(yaw, prim.b) }
            : { ...prim, center: apply(yaw, prim.center), basis: mul(yaw, prim.basis) },
    )

/** The raster a species draws with: its style's, on its frame (centred as a humanoid's bigger frames are), with its changes. */
export const speciesRaster = (s: SpeciesSource, frame = s.frame): RasterOptions => {
    const style = (EMBER_STYLES[s.style ?? "realist"] ?? EMBER_STYLES[DEFAULT_STYLE]).raster
    const { pitch, ...rest } = (s.raster ?? {}) as Record<string, any>
    const grow = (frame - 64) / 2
    return {
        ...style,
        ...rest,
        width: frame,
        height: frame,
        centerX: 32 + grow,
        groundY: 61 + grow,
        ...(typeof pitch === "number" ? { pitch: deg(pitch) } : {}),
    }
}

/** The clip a species plays for an animation's action: its action table's, else one of its own by name. */
const clipFor = (species: EmberSpecies, action: string): SpeciesClip | undefined => {
    const named = hasOwn(species.source.actions, action) ? species.source.actions[action] : action
    return hasOwn(species.clips, named) ? species.clips[named] : undefined
}

/** The rest direction square to a grip that faces most nearly down: a blade's edge by default. */
const edgeFor = (grip: Vec3): Vec3 => {
    const g = normalize(grip)
    const down = sub([0, -1, 0], scale(g, -g[1]))
    return length(down) > 1e-3 ? normalize(down) : normalize(sub([0, 0, 1], scale(g, g[2])))
}

/**
 * A person's skeleton as far as what is held needs one, from a species' posed joints and its grips:
 * each hand, its elbow and shoulder, which way a tool points from it and its edge faces, the torso
 * and pelvis frames. The game's held and shield models run on it unchanged.
 */
export const gripSkeleton = (species: EmberSpecies, sk: SpeciesSkeleton): Skeleton | undefined => {
    const grips = species.source.grips
    if (!grips?.R && !grips?.L) return undefined
    const { rig } = species
    const has = (name: string | undefined) => !!name && hasOwn(rig.index, name)
    const parentOf = (name: string) => {
        const q = rig.parent[rig.index[name]]
        return q >= 0 ? rig.names[q] : name
    }
    const root = rig.names[0]
    const torso = has(grips.torso) ? grips.torso! : has("chest") ? "chest" : root
    const limb = (g: SpeciesGripSource): LimbJoints => {
        const elbow = has(g.elbow) ? g.elbow! : parentOf(g.hand)
        const shoulder = has(g.shoulder) ? g.shoulder! : parentOf(elbow)
        const turn = sk.basis[g.hand]
        const hand = sk.at[g.hand]
        const ground: Vec3 = [hand[0], 0, hand[2]]
        return {
            shoulder: sk.at[shoulder], elbow: sk.at[elbow], hand,
            hip: sk.at[root], knee: sk.at[root], ankle: ground, heel: ground, toe: ground,
            grip: normalize(apply(turn, g.grip)),
            edge: normalize(apply(turn, g.edge ?? edgeFor(g.grip))),
        }
    }
    const R = limb((grips.R ?? grips.L)!)
    const L = grips.L ? limb(grips.L) : R
    const head = has("head") ? "head" : torso
    return {
        pelvis: sk.at[root], chest: sk.at[torso], neck: sk.at[has("neck") ? "neck" : torso], head: sk.at[head],
        pelvisBasis: sk.basis[root], torsoBasis: sk.basis[torso], headBasis: sk.basis[head], L, R,
    }
}

/** A person's proportions as far as what is held is sized by them, at the grips' size. */
const gripProportions = (size = 1): Proportions => ({
    ...DEFAULT_PROPORTIONS,
    thigh: DEFAULT_PROPORTIONS.thigh * size,
    shin: DEFAULT_PROPORTIONS.shin * size,
    handRadius: 1.8 * size,
    handInset: 0.6 * size,
    torso: scale(DEFAULT_PROPORTIONS.torso, size),
})

/** What a species body holds of the entity's equipment: nothing without grips; a shield only in an off hand. */
const heldBy = (species: EmberSpecies, equipment: EmberEquipment): Partial<EmberLook> => {
    const grips = species.source.grips
    if (!grips?.R && !grips?.L) return {}
    return {
        ...(equipment.held && emberDesign("held", equipment.held) ? { held: equipment.held, heldColor: equipment.heldColor } : {}),
        ...(equipment.shield && grips.L && grips.R ? { shield: { type: equipment.shield, color: equipment.shieldColor ?? "" } } : {}),
    }
}
const GEAR_SKIN = rampFrom("#b07a58")

/** Draw a species body in a pose and facing: its model hung on the solved joints, what it holds in its grips, turned and rasterized. */
export const renderSpeciesFrame = (bodyName: string, sk: SpeciesSkeleton, facing: Facing, equipment: EmberEquipment = {}, frame?: number): Frame => {
    const body = EMBER_SPECIES_BODIES[bodyName]
    const species = EMBER_SPECIES[body.species]
    const s = species.source
    const made = runEmberSpeciesModel(body.model, body.params ?? {}, { sk, p: s.proportions ?? {}, facing }) as Primitive[]
    const holds = heldBy(species, equipment)
    const person = Object.keys(holds).length ? gripSkeleton(species, sk) : undefined
    if (person) made.push(...equipmentPrimitives(person, gripProportions(s.grips?.size), { skin: "light", ...holds } as EmberLook, facing, GEAR_SKIN))
    const prims = body.scale && body.scale !== 1 ? scalePrimitives(made, body.scale) : made
    return rasterize(turned(prims, rotY(speciesYaw(facing, deg(s.sideTurn ?? 10)))), speciesRaster(s, frame))
}

/** The frame a species body is drawn on: its own, or bigger for the reach of what it holds (as a humanoid's grows). */
const frameFor = (species: EmberSpecies, equipment: EmberEquipment): number => {
    const s = species.source
    const held = heldBy(species, equipment).held
    if (!held) return s.frame
    const reach = (emberDesign("held", held)?.handling?.frame ?? 96) + (s.frame - 64)
    return Math.min(192, Math.max(s.frame, reach))
}

/**
 * Plan a species body's animation (generate.ts planEmberAnimation hands over any name a species
 * body has): `<action>-<facing>` plays the gait or clip its action table names for the action --
 * or one of its own clips by name -- in that facing. An attack on the move is played as the attack.
 */
/** The species, clip and facing an animation of a species body plays, if it plays one. */
const speciesAnimation = (bodyName: string, animation: string) => {
    const body = EMBER_SPECIES_BODIES[bodyName]
    const species = body && EMBER_SPECIES[body.species]
    if (!species) return undefined
    const [name, direction] = animation.split("-")
    const facing = (direction ?? "front") as Facing
    if (!FACINGS.includes(facing)) return undefined
    const action = name.split(".")[0]
    const clip = clipFor(species, action)
    return clip && { body, species, clip, facing, action }
}

/** Where a species body's joints are at frame `i` of an animation (in model space, unscaled), for tools. */
export const speciesSkeletonAt = (bodyName: string, animation: string, i: number): { skeleton: SpeciesSkeleton; frames: number; clip: string; scale: number } | undefined => {
    const a = speciesAnimation(bodyName, animation)
    if (!a) return undefined
    const frame = Math.max(0, Math.min(a.clip.frames - 1, Math.floor(i)))
    return { skeleton: solveSpecies(a.species.rig, a.clip.sample(clipTime(a.clip, frame, a.clip.frames))), frames: a.clip.frames, clip: a.clip.name, scale: a.body.scale ?? 1 }
}

export const planSpeciesAnimation = (bodyName: string, animation: string, equipment: EmberEquipment = {}): EmberAnimationPlan | undefined => {
    const a = speciesAnimation(bodyName, animation)
    if (!a) return undefined
    const { body, species, clip, facing, action } = a
    const s = species.source
    const sideTurn = deg(s.sideTurn ?? 10)
    const frame = frameFor(species, equipment)
    const raster = speciesRaster(s, frame)
    const k = body.scale ?? 1
    const yaw = rotY(speciesYaw(facing, sideTurn))
    const lpcDuration = LPC_ACTION_DURATION_MS[action]
    const pose = (i: number) => solveSpecies(species.rig, clip.sample(clipTime(clip, i, clip.frames)))
    const at = (v: Vec3 | undefined) => (v ? projectPoint(apply(yaw, scale(v, k)), raster) : { x: raster.centerX, y: raster.groundY })
    const points = s.points ?? {}
    return {
        name: animation,
        clip: clip.name,
        facing,
        frameMs: lpcDuration ? lpcDuration / clip.frames : clip.frameMs,
        loop: clip.loop,
        strideCycle: clip.strideCycle && clip.strideCycle * k * acrossScreen(facing, sideTurn),
        frameCount: clip.frames,
        renderFrame: i => renderSpeciesFrame(bodyName, pose(i), facing, equipment, frame),
        jointsAt: (i): EmberJoints => {
            const sk = pose(i)
            const [l, r] = s.grips?.R || s.grips?.L ? [(s.grips.L ?? s.grips.R)!.hand, (s.grips.R ?? s.grips.L)!.hand] : points.hands ?? []
            // Each leg's lowest joint is its foot, planted at the height it stands at rest.
            const legs = species.rig.legs, names = species.rig.names
            const roots = legs.map(leg => sk.at[names[leg.chain[0]]])
            const middle = scale(roots.reduce((sum, v) => add(sum, v), [0, 0, 0] as Vec3), 1 / Math.max(1, roots.length))
            const feet = legs.map((leg, n) => {
                const low = sk.at[names[leg.chain[leg.chain.length - 1]]]
                return frameFoot(scale([low[0], low[1] - leg.contact[1], low[2]], k), scale(roots[n], k), scale(middle, k), v => apply(yaw, v), raster)
            })
            return { head: at(sk.at[points.head ?? "head"]), hands: [at(l ? sk.at[l] : undefined), at(r ? sk.at[r] : undefined)], feet }
        },
    }
}

bindContent(catalog => (catalog.source as any).emberSpecies, registerEmberSpecies)
